import React from 'react';
import { Button, Checkbox, ComboBox, TextBox } from 'components';
import { typeOfDocument } from 'domains/quotation/constants/information';
import shower from 'assets/images/svg/new-plan/banner/shower.svg';
import arrow from 'assets/images/svg/new-plan/banner/arrow-down.svg';
import { useBanner } from '../../hooks/use-banner';

import './banner.scss';

interface BannerProps {
  isFromCanal: boolean;
  title: string;
  isTaxi: boolean;
}

const Banner: React.FC<BannerProps> = (props: BannerProps) => {
  const { isFromCanal, title, isTaxi } = props;

  const {
    fields,
    handleOnChange,
    documentType,
    handleDocumentType,
    handleSubmit,
    isLoadingForm,
    changeValidState,
    handleChangeMobile,
    formValid,
    handleScrollPlans,
    hasConsent,
    handleChangeHasConsent,
    showNameField,
    isLoadingDNI,
    mainImage,
    mainImageMobile,
  } = useBanner();

  return (
    <div className="banner">
      <div className={`banner__mobile ${mainImageMobile}`}>
        <p className="banner__mobile__title">
          {title}
          <br /> {isFromCanal && 'por choque'}
        </p>

        <div className="banner__mobile__show-plans" onClick={handleScrollPlans}>
          Ver planes
          <img src={arrow} alt="arrow" />
        </div>
      </div>
      <div className="banner__container">
        <div className="banner__container__register">
          <p className="banner__container__register--head">
            Seguro {isTaxi && 'Para Taxi'}
          </p>
          <h1 className="banner__container__register--title">
            Auto Efectivo
            <div className="banner__container__register--image"></div>
          </h1>
          <p className="banner__container__register--sub-title">
            Ingresa tus datos y nos contactaremos contigo
          </p>
          <form
            className="banner__container__register__form"
            onSubmit={handleSubmit}
          >
            <div className="banner__container__register__form__content">
              <div className="banner__container__register__form__content__row">
                <div className="content-dropdown">
                  <ComboBox
                    options={typeOfDocument}
                    placeholder="Documento"
                    id="document-type"
                    selected={documentType}
                    onChange={handleDocumentType}
                  />
                  <TextBox
                    type="text"
                    id="dni"
                    placeholder="Documento"
                    value={fields.dni}
                    onChange={(e: any) =>
                      handleOnChange(e.target.value, 'dni', '')
                    }
                    maxLength={documentType.value === 'DNI' ? 8 : 9}
                    onValidState={(valid: boolean) => {
                      changeValidState('dniValid', valid);
                    }}
                    validators={
                      documentType.value !== 'DNI'
                        ? [
                            {
                              exp: 'required',
                              msg: 'Debe completar este campo',
                            },
                            {
                              exp: /^[0-9N]{9}$/,
                              msg: 'Debe ingresar 9 digitos con la letra N',
                            },
                          ]
                        : [
                            {
                              exp: 'required',
                              msg: 'Debe completar este campo',
                            },
                            {
                              exp: /^[0-9]{8}$/,
                              msg: 'Debe ingresar 8 digitos numéricos',
                            },
                          ]
                    }
                  />
                </div>
                <TextBox
                  type="text"
                  id="phone"
                  placeholder="n° de celular"
                  value={fields.phone}
                  onChange={handleChangeMobile}
                  maxLength={9}
                  validators={[
                    { exp: 'required', msg: 'Debe completar este campo' },
                    {
                      exp: /^9\d{8}$/,
                      msg: 'El número debe iniciar con 9 y tener 9 dígitos',
                    },
                    {
                      exp: /^(?!.*(\d)\1{4})\d+$/,
                      msg: 'Debe ingresar un número de celular válido',
                    },
                  ]}
                  onValidState={(valid: boolean) => {
                    changeValidState('phoneValid', valid);
                  }}
                  cleaveOptions={{
                    options: {
                      blocks: [3, 3, 3],
                    },
                  }}
                />
              </div>

              {showNameField && !isLoadingDNI && (
                <div className="banner__container__register__form__content__row">
                  <TextBox
                    type="text"
                    id="name"
                    placeholder="Nombres y apellido"
                    value={fields.firstName}
                    onChange={(e: any) =>
                      handleOnChange(e.target.value, 'firstName', '')
                    }
                    validators={[
                      { exp: 'required', msg: 'Debe completar este campo' },
                      {
                        exp: /^[A-Za-z ]{2,40}$/,
                        msg: 'Debe tener entre 2 a 40 caracteres',
                      },
                    ]}
                    onValidState={(valid: boolean) => {
                      changeValidState('firstNameValid', valid);
                    }}
                    maxLength={40}
                  />
                </div>
              )}
              <div className="banner__container__register__form__content__row">
                <TextBox
                  type="text"
                  id="lastName"
                  placeholder="Correo electrónico"
                  value={fields.email}
                  onChange={(e: any) =>
                    handleOnChange(e.target.value, 'email', '')
                  }
                  validators={[
                    { exp: 'required', msg: 'Debe completar este campo' },
                    {
                      exp: /^[\w_\-.]+@([\w-]+\.)+[\w-]{2,4}$/,
                      msg: 'Ingresa un correo electrónico válido',
                    },
                  ]}
                  onValidState={(valid: boolean) => {
                    changeValidState('emailValid', valid);
                  }}
                  maxLength={30}
                />
              </div>
              <div className="banner__container__register__form__auth">
                <Checkbox
                  id="autorizationTerms"
                  value={hasConsent}
                  onChange={() => handleChangeHasConsent()}
                />
                <p>
                  He leído y acepto que me contacten en caso no termine la
                  cotización. Conoce más{' '}
                  <a
                    href="https://www.pacifico.com.pe/terminos-condiciones-ecommerce"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    aquí
                  </a>
                </p>
              </div>
              <Button
                label={!isLoadingDNI ? 'Solicítalo aquí' : 'Cargando DNI'}
                className="banner__container__register__form__button"
                onClick={handleSubmit}
                disabled={!formValid || isLoadingForm}
                isLoading={isLoadingForm}
              />
            </div>
          </form>
        </div>
        <div className="banner__container__image">
          <img
            src={mainImage}
            alt="banner"
            className="banner__container__image__background"
          />
          <p className="banner__container__image__title">
            {title}
            <br /> {isFromCanal && 'por choque'}
          </p>
          <div className="banner__container__image__card">
            <img
              src={shower}
              alt="hand"
              className="information__cards__card__image"
            />
            <p>
              Te pagamos para que repares el choque de tu{' '}
              {isTaxi ? 'taxi' : 'carro'} donde quieras.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Banner;

Banner.defaultProps = {
  isFromCanal: false,
  title: '',
  isTaxi: false,
};
